// fichier : mv-modal-quick.js

/**
 * Classe principale qui gère tout le système de vue rapide (Quick View)
 * Elle s'occupe de l'ouverture du modal, du chargement AJAX du produit,
 * et de la gestion des événements de fermeture
 */
class MvQuickView {
  constructor() {
    // Initialisation des propriétés qui stockeront les éléments DOM
    this.modal = null;
    this.loadingEl = null;
    this.contentEl = null;
    this.init();
  }

  /**
   * Point d'entrée de la classe - appelé automatiquement à la construction
   */
  init() {
    // Attache les événements de clic sur tous les boutons quick-view de la page
    this.attachButtonEvents();
    // Fermeture par glissement vers le bas (bottom sheet mobile)
    this.attachSwipeToDismiss();
    // Partage du produit (feuille native mobile + fallback presse-papier)
    this.attachShareEvent();
  }

  /**
   * Bouton « Partager » (haut gauche du panneau). Délégation depuis document car le
   * bouton vit dans le shell persistant, mais on lit les données de partage sur le
   * contenu produit ré-injecté (.quick-view-grid[data-share-url]).
   */
  attachShareEvent() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.quick-view-share');
      if (!btn) return;
      e.preventDefault();
      const modal = document.getElementById('quick-view-modal');
      const grid = modal && modal.querySelector('.quick-view-product-info .quick-view-grid');
      const url = grid && grid.getAttribute('data-share-url');
      if (!url) return;
      const text = (grid.getAttribute('data-share-text') || 'Regarde cette bonne affaire 👀').trim();
      this.shareProduct(url, text);
    });
  }

  /**
   * Ouvre la feuille de partage native (navigator.share) sur mobile ;
   * sinon copie « texte + lien » dans le presse-papier et affiche un toast.
   */
  async shareProduct(url, text) {
    if (navigator.share) {
      try {
        await navigator.share({ text: text, url: url });
        return; // partage réussi → pas de toast
      } catch (err) {
        // L'utilisateur a annulé la feuille de partage : on ne fait rien
        if (err && err.name === 'AbortError') return;
        // Sinon (API présente mais échec) : on bascule sur la copie ci-dessous
      }
    }
    const toCopy = text + ' ' + url;
    try {
      await navigator.clipboard.writeText(toCopy);
    } catch (e) {
      // Fallback ultra : textarea hors-écran + execCommand (vieux navigateurs)
      const ta = document.createElement('textarea');
      ta.value = toCopy;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (_) {}
      document.body.removeChild(ta);
    }
    this.showShareToast();
  }

  /**
   * Toast transitoire « Lien copié ✓ » au-dessus de la barre d'action.
   */
  showShareToast() {
    const panel = document.querySelector('#quick-view-modal .quick-view-content');
    if (!panel) return;
    let toast = panel.querySelector('.quick-view-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'quick-view-toast';
      panel.appendChild(toast);
    }
    toast.textContent = 'Lien copié ✓';
    // Force un reflow pour rejouer la transition si le toast existait déjà
    void toast.offsetWidth;
    toast.classList.add('is-visible');
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 1800);
  }

  /**
   * Swipe-to-dismiss sur le panneau (mobile). Même logique que les bottom sheets
   * du header : le drag ne s'arme que si le contenu est déjà en haut, ou si on
   * attrape la poignée. Le panneau est stable dans le DOM (seul son contenu est
   * ré-injecté), donc on branche l'écoute une seule fois.
   */
  attachSwipeToDismiss() {
    const modal = document.getElementById('quick-view-modal');
    const panel = modal && modal.querySelector('.quick-view-content');
    if (!panel) return;
    const scrollEl = modal.querySelector('.quick-view-body');
    const CLOSE_THRESHOLD = 90; // px avant de fermer
    let startY = 0, curY = 0, dragging = false, armed = false;

    panel.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1) { armed = false; return; }
      const atTop = !scrollEl || scrollEl.scrollTop <= 0;
      const onGrip = e.target.closest('.quick-view-handle');
      armed = atTop || !!onGrip;
      startY = curY = e.touches[0].clientY;
      dragging = false;
    }, { passive: true });

    panel.addEventListener('touchmove', (e) => {
      if (!armed) return;
      curY = e.touches[0].clientY;
      const dy = curY - startY;
      if (dy <= 0) { // vers le haut → on annule un éventuel drag
        if (dragging) { dragging = false; panel.style.transition = ''; panel.style.transform = ''; }
        return;
      }
      if (!dragging && dy > 6) { dragging = true; panel.style.transition = 'none'; }
      if (dragging) {
        if (e.cancelable) e.preventDefault(); // évite le scroll de la page pendant le drag
        panel.style.transform = 'translateY(' + dy + 'px)';
      }
    }, { passive: false });

    const endDrag = () => {
      if (!dragging) { armed = false; return; }
      const dy = curY - startY;
      panel.style.transition = '';
      panel.style.transform = ''; // laisse le CSS (.is-open) reprendre la main
      dragging = false; armed = false;
      if (dy > CLOSE_THRESHOLD) this.closeModal();
    };
    panel.addEventListener('touchend', endDrag);
    panel.addEventListener('touchcancel', endDrag);
  }

  /**
   * Utilise la délégation d'événements pour gérer tous les clics sur les boutons quick-view
   * Avantage : fonctionne même pour les boutons ajoutés dynamiquement après le chargement
   */
  attachButtonEvents() {
    document.addEventListener('click', (e) => {
      // Mini-carte « produit complémentaire » (mono-variante) → ouvre sa quickview
      const recoItem = e.target.closest('.quick-view-reco-item[data-quick-view-handle]');
      if (recoItem) {
        e.preventDefault();
        this.openModal(recoItem.getAttribute('data-quick-view-handle'));
        return;
      }

      // Ancien bouton (i) — conservé au cas où il subsisterait quelque part
      const btn = e.target.closest('.quick-view-btn');
      if (btn) {
        e.preventDefault();
        e.stopPropagation();

        // Récupère le handle du produit depuis l'attribut data-product-handle
        const productHandle = btn.dataset.productHandle;
        if (productHandle) {
          this.openModal(productHandle);
        }
        return;
      }

      // Toute la carte produit (mono-variante) ouvre l'aperçu au tap
      const card = e.target.closest('.mm-product-card[data-quick-view-handle]');
      if (!card) return;

      // Laisse les contrôles panier (bouton +, sélecteur de quantité) et le (i) DDM
      // (ouvre la bottom sheet) gérer leur propre action
      if (e.target.closest('.mm-btn-add-product, .mm-dc-product-qqty, [data-mm-sheet-open]')) return;

      e.preventDefault();
      const productHandle = card.dataset.quickViewHandle;
      if (productHandle) {
        this.openModal(productHandle);
      }
    });
  }

  /**
   * Ouvre le modal et déclenche le chargement du produit via AJAX
   * @param {string} productHandle - Le handle unique du produit (ex: "beurre-bio-250g")
   */
  async openModal(productHandle) {
    // Récupère les éléments DOM du modal (fait une seule fois par ouverture)
    this.modal = document.getElementById('quick-view-modal');
    this.loadingEl = this.modal.querySelector('.quick-view-loading');
    this.contentEl = this.modal.querySelector('.quick-view-product-info');

    // Sécurité : si le modal n'existe pas dans le DOM, on arrête
    if (!this.modal) {
      console.error('Modal quick-view introuvable');
      return;
    }

    // Révèle le panneau (déclenche le slide-up / fade via .is-open)
    this.modal.classList.add('is-open');
    // Ajoute une classe au body pour bloquer le scroll de la page
    document.body.classList.add('quick-view-open');
    // Repart du haut du contenu à chaque ouverture
    const body = this.modal.querySelector('.quick-view-body');
    if (body) body.scrollTop = 0;

    // Annule un éventuel vidage différé d'une fermeture précédente (réouverture rapide)
    if (this._clearTimer) { clearTimeout(this._clearTimer); this._clearTimer = null; }

    // Réinitialise la barre d'action (vidée jusqu'à ce que le produit soit chargé)
    const actionbar = this.modal.querySelector('.quick-view-actionbar');
    if (actionbar) { actionbar.innerHTML = ''; actionbar.hidden = true; actionbar.removeAttribute('data-quick-view-item-id'); }

    // Cache le bouton partage tant que le produit (et donc son lien) n'est pas chargé
    const shareBtn = this.modal.querySelector('.quick-view-share');
    if (shareBtn) shareBtn.hidden = true;

    // Affiche le loader et cache le contenu pendant le chargement
    this.loadingEl.style.display = 'block';
    this.contentEl.style.display = 'none';
    this.contentEl.innerHTML = '';

    try {
      // Lance la requête AJAX pour charger le produit
      await this.loadProduct(productHandle);
    } catch (error) {
      console.error('Erreur lors du chargement du produit:', error);
      // En cas d'erreur, affiche un message d'erreur à l'utilisateur
      this.showError();
    }

    // Attache les événements pour fermer le modal (clic overlay, bouton X, touche Escape)
    this.attachCloseEvents();
  }

  /**
   * Charge le contenu du produit via AJAX et l'injecte dans le modal
   * @param {string} productHandle - Le handle du produit
   */
  async loadProduct(productHandle) {
    // Construit l'URL avec le paramètre ?view=quick-view
    // Shopify utilisera le template product.quick-view.liquid au lieu du template par défaut
    const url = `/products/${productHandle}?view=quick-view`;

    console.log('Chargement du produit:', url); // DEBUG

    // Fait la requête AJAX vers Shopify
    const response = await fetch(url);
    
    // Vérifie que la requête a réussi (statut 200)
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Récupère le HTML retourné par Shopify
    const html = await response.text();
    
    console.log('HTML reçu:', html.substring(0, 200)); // DEBUG - Affiche les 200 premiers caractères

    // Parse le HTML avec DOMParser pour pouvoir le manipuler
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Cherche l'élément .quick-view-grid dans le HTML retourné
    // Cet élément est défini dans product.quick-view.liquid
    const productContent = doc.querySelector('.quick-view-grid');

    console.log('Contenu trouvé:', productContent); // DEBUG

    if (productContent) {
      // Injecte le contenu du produit dans le modal
      this.contentEl.innerHTML = productContent.outerHTML;
      
      // Masque le loader et affiche le contenu
      this.loadingEl.style.display = 'none';
      this.contentEl.style.display = 'block';
      
      // ✅ CRITIQUE : Initialise les dropdowns après l'injection du HTML
      // Sans cette étape, les sections déroulantes ne fonctionneraient pas
      if (typeof window.initQuickViewDropdowns === 'function') {
        window.initQuickViewDropdowns();
      } else {
        console.error('❌ La fonction initQuickViewDropdowns n\'est pas définie');
      }

      // Barre épinglée en bas : MOBILE uniquement (≤768px). Sur desktop (>768px), le
      // prix + CTA reste inline dans la colonne détails, juste au-dessus des « + du
      // produit » → toujours au même endroit quel que soit le produit.
      if (window.matchMedia('(max-width: 768px)').matches) {
        this.mountActionBar();
      }

      // Le produit est chargé (son lien de partage est dispo) → révèle le bouton partage
      const shareBtn = this.modal.querySelector('.quick-view-share');
      if (shareBtn) shareBtn.hidden = false;

      // Charge les « produits complémentaires » en lazy (2e requête, non bloquante)
      this.loadRecommendations();
    } else {
      // Si .quick-view-grid n'est pas trouvé, affiche le HTML complet pour debug
      console.error('Sélecteur .quick-view-grid introuvable dans:', doc.body.innerHTML);
      throw new Error('Contenu du produit introuvable - Vérifier que product.quick-view.liquid existe');
    }
  }

  /**
   * Déplace le bloc prix + CTA (injecté dans le contenu) vers la barre épinglée
   * du bas. La barre porte la classe .quick-view-grid, donc les styles du CTA
   * (scopés .quick-view-grid ...) restent appliqués après le déplacement.
   */
  mountActionBar() {
    const bar = this.modal && this.modal.querySelector('.quick-view-actionbar');
    if (!bar) return;
    const grid = this.contentEl.querySelector('.quick-view-grid[data-quick-view-item-id]');
    const cta = this.contentEl.querySelector('.quick-view-price-cta-container');
    const trust = this.contentEl.querySelector('.quick-view-trust');
    bar.innerHTML = '';
    bar.removeAttribute('data-quick-view-item-id');
    if (cta) {
      // Transfère l'id variant de la grille vers la barre : refreshQuickViewButtons
      // (mm_cart.js) retrouve ainsi le bouton/sélecteur déplacés pour basculer
      // « Ajouter » ↔ sélecteur +/− après un ajout au panier.
      // On le retire de la grille sinon querySelector renverrait la grille (1er match).
      if (grid) {
        const id = grid.getAttribute('data-quick-view-item-id');
        if (id) {
          bar.setAttribute('data-quick-view-item-id', id);
          grid.removeAttribute('data-quick-view-item-id');
        }
      }
      bar.appendChild(cta);
      bar.hidden = false;
    } else {
      bar.hidden = true;
    }
    // Ligne de réassurance SOUS le CTA (la barre est en colonne)
    if (trust) {
      bar.appendChild(trust);
      bar.hidden = false;
    }
  }

  /**
   * Charge les « produits complémentaires » (algo Shopify `related`) via l'API de
   * recommandations, rendus par la section mm-quickview-reco, puis les injecte dans
   * le conteneur .quick-view-reco. Non bloquant : si aucune reco, le bloc reste caché.
   */
  async loadRecommendations() {
    const container = this.contentEl.querySelector('.quick-view-reco[data-product-id]');
    if (!container) return;
    const pid = container.getAttribute('data-product-id');
    const slot = container.querySelector('.quick-view-reco-slot');
    if (!pid || !slot) return;
    try {
      const url = `/recommendations/products?product_id=${pid}&limit=8&intent=related&section_id=mm-quickview-reco`;
      const res = await fetch(url);
      if (!res.ok) return;
      const text = await res.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const rail = doc.querySelector('[data-quick-view-reco-rail]');
      if (!rail || rail.children.length === 0) return; // pas de reco → on laisse caché
      slot.innerHTML = rail.outerHTML;
      container.hidden = false;

      // Indice de scroll : dégradé de fondu sur les bords (classe .mm-horizontal-fade-rail),
      // togglé selon la position — montre qu'il reste des produits à droite.
      const railEl = slot.querySelector('.quick-view-reco-rail');
      if (railEl) {
        const updateFade = () => {
          const maxScroll = Math.max(0, railEl.scrollWidth - railEl.clientWidth);
          railEl.classList.toggle('has-scroll-left', railEl.scrollLeft > 2);
          railEl.classList.toggle('has-scroll-right', railEl.scrollLeft < maxScroll - 2);
        };
        railEl.addEventListener('scroll', updateFade, { passive: true });
        requestAnimationFrame(updateFade);
      }
    } catch (e) { /* silencieux : la reco est un bonus, pas critique */ }
  }

  /**
   * Affiche un message d'erreur utilisateur en cas de problème de chargement
   */
  showError() {
    this.contentEl.innerHTML = `
      <div style="text-align: center; padding: 40px;">
        <p style="color: #e74c3c; font-size: 16px; margin-bottom: 10px;">
          Une erreur est survenue lors du chargement du produit.
        </p>
        <p style="color: #666; font-size: 14px;">
          Veuillez réessayer ou visiter la page produit directement.
        </p>
      </div>
    `;
    
    // Masque le loader et affiche le message d'erreur
    this.loadingEl.style.display = 'none';
    this.contentEl.style.display = 'block';
  }

  /**
   * Attache les trois façons de fermer le modal :
   * 1. Clic sur le bouton X
   * 2. Clic sur l'overlay (fond sombre)
   * 3. Touche Escape du clavier
   */
  attachCloseEvents() {
    const closeBtn = this.modal.querySelector('.quick-view-close');
    const overlay = this.modal.querySelector('.quick-view-overlay');

    // Fonction de fermeture réutilisable
    const closeModal = () => {
      this.closeModal();
    };

    // Clic sur le bouton X
    if (closeBtn) {
      closeBtn.onclick = closeModal;
    }

    // Clic sur l'overlay (fond sombre autour du modal)
    if (overlay) {
      overlay.onclick = closeModal;
    }

    // Touche Escape - avec suppression de l'événement après fermeture
    const escapeHandler = (e) => {
      if (e.key === 'Escape') {
        closeModal();
        // Nettoie l'événement pour éviter les fuites mémoire
        document.removeEventListener('keydown', escapeHandler);
      }
    };
    document.addEventListener('keydown', escapeHandler);
  }

  /**
   * Ferme le modal et nettoie le contenu
   */
  closeModal() {
    if (this.modal) {
      // Referme le panneau (slide-down via .is-open)
      this.modal.classList.remove('is-open');
      // Réactive le scroll de la page
      document.body.classList.remove('quick-view-open');

      // Vide le contenu ET la barre d'action APRÈS l'animation de fermeture (évite le saut visuel)
      const el = this.contentEl;
      const bar = this.modal.querySelector('.quick-view-actionbar');
      this._clearTimer = setTimeout(() => {
        if (el) el.innerHTML = '';
        if (bar) { bar.innerHTML = ''; bar.hidden = true; bar.removeAttribute('data-quick-view-item-id'); }
        this._clearTimer = null;
      }, 300);
    }
  }
}

// Initialisation automatique au chargement de la page
function initMvQuickView() {
  // Instance exposée globalement (utile pour le deep-link et d'éventuels appels externes)
  window.mvQuickView = new MvQuickView();

  // Deep-link : une URL partagée ?quickview={handle} ouvre directement la preview du produit,
  // par-dessus la page collection (sous-rayon) sur laquelle elle atterrit.
  try {
    const params = new URLSearchParams(window.location.search);
    const handle = params.get('quickview');
    if (handle) {
      window.mvQuickView.openModal(handle);
      // Retire quickview/ref de l'URL : évite la ré-ouverture au refresh et garde une URL propre
      params.delete('quickview');
      params.delete('ref');
      const query = params.toString();
      const clean = window.location.pathname + (query ? '?' + query : '') + window.location.hash;
      window.history.replaceState({}, '', clean);
    }
  } catch (e) { /* URLSearchParams indispo : on ignore silencieusement */ }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMvQuickView);
} else {
  // Si déjà chargé (script defer ou async), initialiser directement
  initMvQuickView();
}
